import { useEffect, useState, type ReactNode } from "react";
import { MeldenKnopf } from "./Melden";
import { Link } from "react-router";
import { ApiError } from "../data";
import { t } from "../lib/sprache";
import { fehlerText } from "../lib/fehler";
import { einrichtungsLuecke } from "../lib/einrichtung";
import { EigenerKopf } from "./shell/Logo";
import { NurLesenHinweis, useNurLesen, useNurLesenPause } from "../app/nurLesen";

/** Lade-, Leer- und Fehlerzustände nach der Zustandsbibliothek des Design-Pakets (15_texte.md). */

export function Loading({ label = t("Wird geladen …", "Loading …") }: { label?: string }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 300);
    return () => clearTimeout(t);
  }, []);
  return (
    <main id="main" className="content" aria-busy="true">
      {show && <p className="muted tk-loading">{label}</p>}
    </main>
  );
}

export type CocoPose = "winkt" | "erklaert" | "hinweis" | "freut-sich" | "kopf" | "lugt";

/**
 * Coco, das Maskottchen (docs/COCO.md): Posen aus tools/coco.mjs in /coco/; rein schmückend, daher ohne Alternativtext.
 * eager (K-573): im Rundgang und in „Coco erklärt“ sofort laden; „lazy“ lud im verborgenen bzw. gerade erst eingeblendeten Dialog
 * auf staging nach 7 s noch nicht, neben „Hi, ich bin Coco!“ blieb die Fläche leer. Leere Zustände in Listen bleiben „lazy“.
 */
export function Coco({ pose, size = 72, eager = false, className }: { pose: CocoPose; size?: number; eager?: boolean; className?: string }) {
  return <img className={className ? `tk-coco ${className}` : "tk-coco"} src={cocoSrc(pose)} width={size} height={size} alt="" aria-hidden="true" loading={eager ? "eager" : "lazy"} decoding={eager ? "sync" : "async"} />;
}

export const cocoSrc = (pose: CocoPose) => `/coco/coco-${pose}.svg`;

/** Posen vorladen (Rundgang startet): die Blase zeigt Coco ab dem ersten Bild, auch beim Wechsel der Pose */
const vorgeladen = new Set<CocoPose>();
export function cocoVorladen(posen: readonly CocoPose[]): void {
  if (typeof Image === "undefined") return;
  for (const p of posen) {
    if (vorgeladen.has(p)) continue;
    vorgeladen.add(p);
    const img = new Image();
    img.decoding = "async";
    img.src = cocoSrc(p);
  }
}

export function EmptyState({ title, text, action, leseTitel }: {
  title: string; text: string; action?: ReactNode;
  /** K-2: neutraler Titel in Pause und Nur-Lesen, wenn der normale zum Anlegen einlädt („Hier beginnt dein nächster Deal.“) */
  leseTitel?: string;
}) {
  // 0425/K-2: in Pause und Nur-Lesen lädt ein leerer Zustand mit Aktion nicht zum Anlegen ein: weder Knopf noch Einladungstext,
  // sondern der gemeinsame Hinweis „Nur lesen.“ plus Satz
  const nurLesen = useNurLesen();
  if (nurLesen && action) {
    return (
      <div className="panel stack tk-empty">
        <Coco pose="winkt" />
        <h3>{leseTitel ?? title}</h3>
        <NurLesenHinweis />
      </div>
    );
  }
  return (
    <div className="panel stack tk-empty">
      <Coco pose="winkt" />
      <h3>{title}</h3>
      <p>{text}</p>
      {action}
    </div>
  );
}

export function describeError(error: unknown): { title: string; text: string } {
  if (error instanceof ApiError && error.forbidden) {
    return { title: t("Diese Aktion ist nicht freigegeben.", "This action is not permitted."), text: t("Wende dich an deinen Admin.", "Contact your admin.") };
  }
  const message = error instanceof Error ? error.message : String(error);
  // K-499 c: fehlende Eingabe (etwa die B2B-Bestätigung) ist kein Fehlschlag; nur der Hinweis, ohne „Das hat nicht geklappt.“
  if ((error as { eingabe?: boolean } | null)?.eingabe) return { title: "", text: message };
  if (/fetch|network|Failed to/i.test(message)) {
    return { title: t("Verbindung fehlgeschlagen.", "Connection failed."), text: t("Deine Eingaben bleiben erhalten. Versuche es erneut.", "Your entries are kept. Please try again.") };
  }
  return { title: t("Das hat nicht geklappt.", "That did not work."), text: fehlerText(error) };
}

/**
 * Probelauf H1: Die Agentur ist noch nicht fertig eingerichtet (fehlende Einstellungen). Statt eines Fehlers oder eines
 * endlosen „Wird geladen …“ ein Hinweis mit dem Weg zur Einrichtung.
 */
export function EinrichtungOffen({ error }: { error?: unknown }) {
  const l = einrichtungsLuecke(error) ?? einrichtungsLuecke("SETTING_MISSING: org_settings");
  // 0425 (S-6): in der Pause bzw. im Nur-Lesen-Zustand lässt sich nichts einrichten: keine Einrichtungssperre, ein ruhiger Hinweis
  const nurLesen = useNurLesen();
  const pauseBis = useNurLesenPause();
  if (nurLesen) {
    return (
      <main id="main" className="content">
        <div className="panel stack tk-empty" role="status">
          <h1 className="tk-state-title">{t("Für diese Seite fehlen noch Angaben der Agentur.", "This page still needs details from the agency.")}</h1>
          <p>{pauseBis
            ? t(`Es fehlt noch: ${l!.label}. Während der Pause (bis ${pauseBis}) lässt sich das nicht ergänzen; danach ist es in einer Minute erledigt. Export und Abo & Tarif gehen weiter.`,
              `Still missing: ${l!.label}. During the pause (until ${pauseBis}) this cannot be added; afterwards it takes a minute. Export and Subscription & plan still work.`)
            : t(`Es fehlt noch: ${l!.label}. Solange das Konto nur lesbar ist, lässt sich das nicht ergänzen. Export und Abo & Tarif gehen weiter.`,
              `Still missing: ${l!.label}. While the account is read-only this cannot be added. Export and Subscription & plan still work.`)}</p>
          <div className="row">
            <Link className="btn" to="/agentur/einstellungen/abo">{t("Abo & Tarif", "Subscription & plan")}</Link>
            <Link className="btn" to="/agentur/einstellungen/export">{t("Daten exportieren", "Export data")}</Link>
          </div>
        </div>
      </main>
    );
  }
  return (
    <>
    <EigenerKopf />
    <main id="main" className="content">
      <div className="panel stack tk-empty" role="status">
        <h1 className="tk-state-title">{t("Die Einrichtung der Agentur ist noch nicht abgeschlossen.", "The agency setup is not complete yet.")}</h1>
        <p>{t(`Es fehlt noch: ${l!.label}. Das ist eine Angabe in den Einstellungen und in einer Minute erledigt; danach zeigt diese Seite ihre Inhalte. Das erledigt ein Admin der Agentur.`,
          `Still missing: ${l!.label}. This is one detail in the settings and takes a minute; after that this page shows its content. An admin of the agency does this.`)}</p>
        <div className="row">
          {/* Inhaber 13.10.2026: direkt in die passende Einstellungsseite statt in die Schrittfolge der Einrichtung */}
          <Link className="btn primary" to={`/agentur/einstellungen/${l!.section}`}>{t(`${l!.label} jetzt festlegen`, `Set ${l!.label} now`)}</Link>
          <Link className="btn" to="/agentur">{t("Alle Schritte ansehen", "See all steps")}</Link>
        </div>
      </div>
    </main>
    </>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  if (einrichtungsLuecke(error)) return <EinrichtungOffen error={error} />;
  const d = describeError(error);
  return (
    <>
    <EigenerKopf />
    <main id="main" className="content">
      <div className="panel stack tk-empty" role="alert">
        <h1 className="tk-state-title">{d.title}</h1>
        <p>{d.text}</p>
        <div className="row">
          {retry && <button onClick={retry}>{t("Erneut versuchen", "Try again")}</button>}
          <Link className="btn" to="/">{t("Zur Startseite", "Go to home page")}</Link>
          <MeldenKnopf message={`${d.title} ${d.text}`.trim()} className="" />
        </div>
      </div>
    </main>
    </>
  );
}

export function NoAccess({ text = t("Wende dich an deinen Admin.", "Contact your admin.") }: { text?: string }) {
  return (
    <>
    <EigenerKopf />
    <main id="main" className="content">
      <div className="panel stack tk-empty" role="alert">
        <h1 className="tk-state-title">{t("Diese Seite ist nicht freigegeben.", "This page is not available to you.")}</h1>
        <p>{text}</p>
        <Link className="btn" to="/">{t("Zur Startseite", "Go to home page")}</Link>
      </div>
    </main>
    </>
  );
}
